import React, { useState, useEffect, useRef } from 'react'
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseClaimService from '../../services/expenseClaimService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ExpenseApproval = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalApprovals: 0,
    pendingApprovals: 0,
    approvedApprovals: 0,
    rejectedApprovals: 0,
    partiallyApproved: 0,
    totalPendingAmount: 0,
    totalApprovedAmount: 0
  })
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionItem, setActionItem] = useState(null)
  const [actionType, setActionType] = useState(null) // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      // Fetch all submitted expense claims (exclude Draft and Cancelled)
      // This will show Submitted, Under Review, Approved, Rejected, Partially Approved
      const [claimsResponse, statsResponse] = await Promise.all([
        expenseClaimService.getAllClaims(), // Get all claims, we'll filter out Draft
        expenseClaimService.getClaimStats()
      ])
      
      if (claimsResponse.success) {
        // Filter out Draft and Cancelled claims, only show submitted ones
        const submittedClaims = (claimsResponse.data || []).filter(claim => 
          claim.status !== 'Draft' && claim.status !== 'Cancelled'
        )
        
        // Transform _id to id for frontend compatibility
        const transformedData = submittedClaims.map(claim => ({
          ...claim,
          id: claim._id,
          claimAmount: claim.grandTotal || claim.totalAmount || 0,
          submittedDate: claim.submittedDate || claim.claimDate,
          priority: claim.priority || 'Medium',
          approvalStatus: claim.status // Map status to approvalStatus for compatibility
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (submittedClaims.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No submitted expense claims found.')
        }
      } else {
        toast.error(claimsResponse.message || 'Failed to load approvals')
      }
      
      if (statsResponse.success) {
        // Map stats to match the expected structure
        setStats({
          pendingApprovals: statsResponse.data.submittedClaims || 0,
          approvedApprovals: statsResponse.data.approvedClaims || 0,
          rejectedApprovals: statsResponse.data.rejectedClaims || 0,
          totalPendingAmount: statsResponse.data.pendingReimbursement || 0,
          totalApprovedAmount: 0 // Can be calculated if needed
        })
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading expense approval records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view approvals')
      } else {
        toast.error('Failed to load expense approval records')
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'Urgent': 'danger',
      'High': 'warning',
      'Medium': 'pending',
      'Low': 'inactive'
    }
    return priorityMap[priority] || 'inactive'
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Under Review': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Partially Approved': 'warning',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'claimNumber',
      header: 'Claim Number',
      meta: { align: 'center' },
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      meta: { align: 'center' },
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'center' }}>
          <div className="description-text">{getValue() || '-'}</div>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'grandTotal',
      header: 'Claim Amount',
      meta: { align: 'center' },
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {formatCurrency(getValue() || row.original.claimAmount || 0, row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { align: 'center' },
      cell: ({ getValue }) => {
        const status = getValue()
        const badgeClass = getStatusBadgeClass(status)
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span className={`status-badge ${badgeClass}`}>
              {status || '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'submittedDate',
      header: 'Submitted Date',
      meta: { align: 'center' },
      cell: ({ getValue, row }) => {
        const date = getValue() || row.original.submittedDate || row.original.claimDate
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'approverName',
      header: 'Approver',
      meta: { align: 'center' },
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          <span>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleView = (claimData) => {
    navigate('/expenses/expense-claim/view', { state: { claimData } })
  }

  const handleApprove = (claimData) => {
    if (claimData.status !== 'Submitted' && claimData.status !== 'Under Review') {
      toast.warning('Only submitted claims can be approved')
      return
    }
    setActionItem(claimData)
    setActionType('approve')
    setShowActionModal(true)
  }

  const handleReject = (claimData) => {
    if (claimData.status !== 'Submitted' && claimData.status !== 'Under Review') {
      toast.warning('Only submitted claims can be rejected')
      return
    }
    setActionItem(claimData)
    setActionType('reject')
    setRejectionReason('')
    setShowActionModal(true)
  }

  const confirmAction = async () => {
    if (!actionItem) return

    try {
      if (actionType === 'approve') {
        toast.info('Approving expense claim...')
        // Update claim status to Approved
        const response = await expenseClaimService.updateClaim(actionItem._id, {
          status: 'Approved',
          approvedDate: new Date(),
          approvalNotes: 'Approved by approver'
        })
        if (response.success) {
          toast.success('Expense claim approved successfully!', { autoClose: 2000 })
          loadData() // Reload data
        } else {
          toast.error(response.message || 'Failed to approve expense claim')
        }
      } else if (actionType === 'reject') {
        if (!rejectionReason.trim()) {
          toast.error('Please provide a rejection reason')
          return
        }
        toast.info('Rejecting expense claim...')
        // Update claim status to Rejected
        const response = await expenseClaimService.updateClaim(actionItem._id, {
          status: 'Rejected',
          rejectedDate: new Date(),
          rejectionReason: rejectionReason
        })
        if (response.success) {
          toast.success('Expense claim rejected successfully!', { autoClose: 2000 })
          loadData() // Reload data
        } else {
          toast.error(response.message || 'Failed to reject expense claim')
        }
      }
    } catch (error) {
      console.error(`Error ${actionType === 'approve' ? 'approving' : 'rejecting'} expense claim:`, error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || `Cannot ${actionType} this claim`)
      } else {
        toast.error(`Failed to ${actionType} expense claim. Please try again.`)
      }
    } finally {
      setShowActionModal(false)
      setActionItem(null)
      setActionType(null)
      setRejectionReason('')
    }
  }

  const cancelAction = () => {
    setShowActionModal(false)
    setActionItem(null)
    setActionType(null)
    setRejectionReason('')
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Approvals',
      path: '/expenses/expense-approval',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Expense Approvals?</h3>
      <p>This screen shows <span className="help-term">expense claims submitted for approval</span>. Approvers can review, approve, or reject claims. Only claims that are not Draft or Cancelled appear here.</p>

      <h3>Navigation</h3>
      <p>Use the breadcrumb <span className="help-term">Expenses</span> to go back to the Expenses hub. No separate back arrow is shown; navigation is through the breadcrumb only.</p>

      <h3>Summary cards</h3>
      <ul>
        <li><strong>Pending Approvals</strong> – Number of claims awaiting your review.</li>
        <li><strong>Approved</strong> – Total claims you (or the system) have approved.</li>
        <li><strong>Rejected</strong> – Total claims rejected.</li>
        <li><strong>Pending Amount</strong> – Total amount (₹) still awaiting approval.</li>
      </ul>

      <h3>Table columns</h3>
      <ul>
        <li><strong>Claim Number</strong> – Unique claim reference.</li>
        <li><strong>Employee</strong> – Who raised the claim (and code).</li>
        <li><strong>Claim Amount</strong> – Total amount claimed.</li>
        <li><strong>Status</strong> – Submitted, Under Review, Approved, Rejected, or Partially Approved.</li>
        <li><strong>Submitted Date</strong> – When the claim was submitted.</li>
        <li><strong>Approver</strong> – Who is (or was) responsible for approval.</li>
      </ul>

      <h3>Actions</h3>
      <p>Use the <strong>Action</strong> menu on each row: <strong>View Details</strong> opens the full claim. For claims with status <em>Submitted</em> or <em>Under Review</em>, you can <strong>Approve</strong> or <strong>Reject</strong>. Rejection requires a reason. Use the search bar to filter claims.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Expense Approvals</h1>
          <p>Expense approval and rejection workflows</p>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.pendingApprovals}</span>
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingApprovals}</div>
          <div className="metric-title">Pending Approvals</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.approvedApprovals}</span>
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedApprovals}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Total approved</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.rejectedApprovals}</span>
            </div>
            <div className="metric-trend negative">
              <span>Rejected</span>
            </div>
          </div>
          <div className="metric-value">{stats.rejectedApprovals}</div>
          <div className="metric-title">Rejected</div>
          <div className="metric-subtitle">Total rejected</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{formatCurrency(stats.totalPendingAmount)}</span>
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalPendingAmount)}</div>
          <div className="metric-title">Pending Amount</div>
          <div className="metric-subtitle">Awaiting approval</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onExport={handleExport}
          customActions={(row) => {
            const actions = [
              { type: 'view', label: 'View Details', onClick: () => handleView(row) },
            ]
            
            // Only show Approve and Reject for Submitted or Under Review claims
            if (row.status === 'Submitted' || row.status === 'Under Review') {
              actions.push(
                { type: 'approve', label: 'Approve', icon: <CheckCircle size={16} />, onClick: () => handleApprove(row) },
                { type: 'reject', label: 'Reject', icon: <XCircle size={16} />, onClick: () => handleReject(row) }
              )
            }
            
            return actions
          }}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={showActionModal}
        onClose={cancelAction}
        onConfirm={confirmAction}
        title={actionType === 'approve' ? 'Approve Expense Claim' : 'Reject Expense Claim'}
        message={
          actionType === 'approve' 
            ? `Are you sure you want to approve claim "${actionItem?.claimNumber}"?`
            : (
              <div>
                <p>Are you sure you want to reject claim "${actionItem?.claimNumber}"?</p>
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Rejection Reason <span style={{ color: 'red' }}>*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejection"
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
              </div>
            )
        }
        confirmText={actionType === 'approve' ? 'Approve' : 'Reject'}
        cancelText="Cancel"
        type={actionType === 'approve' ? 'success' : 'danger'}
      />

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Expense Approvals – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ExpenseApproval

